/**
 * Step 9: Package Recommendation Summary
 * Shows the tier recommendation and package selection for community onboarding
 */

import React, { useEffect, useRef } from 'react';
import { CheckCircle, Award, Star, Rocket } from 'lucide-react';
import { StepComponentProps } from '../../../../../types/onboarding';
import { TIER_DETAILS, TIER_BENEFITS } from '../../../../../constants/onboarding/tierConfiguration';
import type { TierType } from '../../../../../constants/onboarding/tierConfiguration';
import { InfoTooltip } from '../../../../shared/tooltip/InfoTooltip';
import { getTooltipContent } from '../../../../../constants/onboarding';

export const Step9Recommendation: React.FC<StepComponentProps> = ({
  formData,
  errors,
  onFieldChange,
  progressiveDisclosure,
}) => {
  // Get the recommended tier from progressive disclosure or default to silver
  const recommendedTier: TierType = progressiveDisclosure.recommendedTier || 'silver';
  // Use the selected tier if user has made a selection, otherwise use recommended
  const selectedTier: TierType = (formData.selectedTier as TierType) || recommendedTier;
  const recommendedDetails = TIER_DETAILS[recommendedTier];

  // Track if data has been initialized to prevent re-runs
  const isInitialized = useRef(false);

  // NEW: Capture recommendation data when component mounts or recommendation changes
  useEffect(() => {
    // Only run once or when key dependencies actually change
    const currentKey = `${recommendedTier}-${formData.totalUnits}-${formData.budgetRange}-${formData.selectedTier}`;

    if (!isInitialized.current && progressiveDisclosure.getRecommendationReasoning) {
      const reasoning = progressiveDisclosure.getRecommendationReasoning();

      // Calculate estimated value based on community size and recommended tier
      const calculateEstimatedValue = () => {
        const totalUnits = parseInt(formData.totalUnits) || 0;
        if (totalUnits === 0) return 0;

        const rates = {
          silver: { monthly: 5, setup: 30 },
          gold: { monthly: 8, setup: 60 },
          custom: { monthly: 12, setup: 100 }, // Estimated for custom
        };

        const rate = rates[recommendedTier];
        const annualValue = totalUnits * rate.monthly * 12;
        const setupValue = totalUnits * rate.setup;

        return annualValue + setupValue;
      };

      const estimatedValue = calculateEstimatedValue();

      // Determine if requires review (custom tier or high-value deals)
      const requiresReview =
        recommendedTier === 'custom' ||
        estimatedValue > 100000 ||
        (formData.budgetRange &&
          ['100k-200k', '200k-plus'].includes(formData.budgetRange as string));

      // Store all recommendation data in form state
      onFieldChange('recommendedTier', recommendedTier);
      onFieldChange('recommendationReasoning', reasoning);
      onFieldChange('estimatedValue', estimatedValue);
      onFieldChange('formVersion', 'v2');
      onFieldChange('requiresReview', requiresReview);

      // Ensure selected tier is also stored (in case user hasn't explicitly selected)
      if (!formData.selectedTier) {
        onFieldChange('selectedTier', recommendedTier);
      }

      isInitialized.current = true;
    }
  }, [recommendedTier, formData.totalUnits, formData.budgetRange, formData.selectedTier]);

  // Calculate actual pricing based on parcel count (ADDED: This connects to Step 1 parcel count)
  const calculatePricing = (tier: TierType, parcels: number) => {
    if (tier === 'custom') {
      return {
        monthlyTotal: 'Contact for pricing',
        setupFee: 'Contact for pricing',
        perParcelMonthly: 'Custom',
        perParcelSetup: 'Custom',
      };
    }

    const rates = {
      silver: { monthly: 5, setup: 30 },
      gold: { monthly: 8, setup: 60 },
    };
    const rate = rates[tier];
    const monthlyTotal = parcels * rate.monthly;
    const setupFee = parcels * rate.setup;

    return {
      monthlyTotal: `$${monthlyTotal.toLocaleString()}`,
      setupFee: `$${setupFee.toLocaleString()}`,
      perParcelMonthly: `$${rate.monthly}`,
      perParcelSetup: `$${rate.setup}`,
    };
  };

  // Get parcel count from Step 1 form data
  const parcelCount = parseInt(formData.totalUnits) || 0;
  const pricing = calculatePricing(recommendedTier, parcelCount);

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-linear-to-br from-green-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Star className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Perfect Match Found!</h2>
        <p className="text-lg text-slate-600">
          Based on your community's needs and goals, we recommend the package that will deliver the
          most value.
        </p>
      </div>

      {/* Recommended Package Card */}
      <div className={`bg-linear-to-r ${recommendedDetails.color} text-white rounded-2xl p-8 mb-8`}>
        <div className="text-center">
          <Star className="w-12 h-12 mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-2">Recommended: {recommendedDetails.name}</h3>
          <p className="text-lg mb-2">{pricing.monthlyTotal}/month</p>
          {parcelCount > 0 && (
            <p className="text-sm mb-4">
              {pricing.perParcelMonthly}/parcel/month • {parcelCount} parcels
            </p>
          )}
          <div className="grid md:grid-cols-3 gap-4">
            {recommendedDetails.highlights.map((highlight, index) => (
              <div key={index} className="text-center">
                <CheckCircle className="w-6 h-6 mx-auto mb-2" />
                <p className="text-sm">{highlight}</p>
              </div>
            ))}
          </div>
          {parcelCount > 0 && (
            <div className="mt-4 p-3 bg-white/20 rounded-lg">
              <p className="text-sm">
                <strong>Setup Fee:</strong> {pricing.setupFee}
                {recommendedTier !== 'custom' && ` (${pricing.perParcelSetup}/parcel)`}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Package Options - ALL THREE TIERS */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {Object.entries(TIER_DETAILS).map(([tier, details]) => {
          const tierPricing = calculatePricing(tier as TierType, parcelCount);
          return (
            <div
              key={tier}
              className={`rounded-xl border-2 p-6 transition-all hover:shadow-lg ${
                tier === selectedTier
                  ? 'border-green-500 bg-green-50 shadow-lg'
                  : tier === recommendedTier
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 bg-white hover:border-blue-300'
              }`}
            >
              <div className="text-center mb-4">
                {tier === 'silver' && <Award className="w-8 h-8 text-gray-600 mx-auto mb-2" />}
                {tier === 'gold' && <Star className="w-8 h-8 text-yellow-600 mx-auto mb-2" />}
                {tier === 'custom' && <Rocket className="w-8 h-8 text-purple-600 mx-auto mb-2" />}

                <div className="flex items-center justify-center gap-2 mb-1">
                  <h4 className="text-xl font-bold text-slate-900">{details.name}</h4>
                  {tier === 'silver' && (
                    <InfoTooltip
                      {...getTooltipContent('silver-package')}
                      content={
                        getTooltipContent('silver-package')?.content ||
                        'Information about the Silver package'
                      }
                      iconSize={16}
                    />
                  )}
                  {tier === 'gold' && (
                    <InfoTooltip
                      {...getTooltipContent('gold-package')}
                      content={
                        getTooltipContent('gold-package')?.content ||
                        'Information about the Gold package'
                      }
                      iconSize={16}
                    />
                  )}
                  {tier === 'custom' && (
                    <InfoTooltip
                      {...getTooltipContent('custom-package')}
                      content={
                        getTooltipContent('custom-package')?.content ||
                        'Information about custom packages'
                      }
                      iconSize={16}
                    />
                  )}
                </div>
                <p className="text-lg font-semibold text-blue-600 mb-2">
                  {tierPricing.monthlyTotal}/month
                </p>
                {parcelCount > 0 && tier !== 'custom' && (
                  <p className="text-xs text-slate-500 mb-2">
                    {tierPricing.perParcelMonthly}/parcel • Setup: {tierPricing.setupFee}
                  </p>
                )}

                {tier === selectedTier && tier === recommendedTier && (
                  <div className="bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full mb-3">
                    ✅ Selected (Recommended)
                  </div>
                )}
                {tier === selectedTier && tier !== recommendedTier && (
                  <div className="bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full mb-3">
                    ✅ Selected
                  </div>
                )}
                {tier === recommendedTier && tier !== selectedTier && (
                  <div className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full mb-3">
                    💡 Recommended for You
                  </div>
                )}
              </div>

              <div className="space-y-3 mb-6">
                {TIER_BENEFITS[tier as TierType].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <benefit.icon className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-slate-800">{benefit.title}</p>
                      <p className="text-sm text-slate-600">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => onFieldChange('selectedTier', tier)}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                  tier === selectedTier
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : tier === recommendedTier
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {tier === selectedTier
                  ? '✅ Selected'
                  : tier === 'custom'
                    ? 'Contact Sales'
                    : `Select ${details.name}`}
              </button>
            </div>
          );
        })}
      </div>

      {/* Why This Recommendation */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <h4 className="text-lg font-semibold text-slate-800 mb-4">
          Why We Recommend the {recommendedDetails.name}
        </h4>
        <div className="space-y-3 text-slate-600">
          {/* Community Size Match */}
          {formData.totalUnits && (
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
              <p>Perfect fit for your community size of {formData.totalUnits} parcels</p>
            </div>
          )}

          {/* Progressive Disclosure Analysis */}
          {progressiveDisclosure.getRecommendationReasoning &&
            (() => {
              const reasoning = progressiveDisclosure.getRecommendationReasoning();
              const { silverScore, goldScore, customScore } = reasoning.interestAnalysis;

              return [
                // Primary tier alignment
                recommendedTier === 'silver' && silverScore > 0 && (
                  <div key="silver-match" className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                    <p>
                      Your focus on cost savings and basic management needs aligns perfectly with
                      our Silver package core strengths
                    </p>
                  </div>
                ),
                recommendedTier === 'gold' && goldScore > 0 && (
                  <div key="gold-match" className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                    <p>
                      Your advanced needs for analytics, investment opportunities, and
                      multi-property management require our Gold package features
                    </p>
                  </div>
                ),
                recommendedTier === 'custom' && customScore > 0 && (
                  <div key="custom-match" className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                    <p>
                      Your enterprise-scale requirements and custom integration needs call for our
                      fully customizable solution
                    </p>
                  </div>
                ),

                // Specific interest-based reasons
                progressiveDisclosure.detectedInterests.includes('cost-reduction') && (
                  <div key="cost-reduction" className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                    <p>
                      Includes group buying features designed to help communities save significantly
                      on vendor services
                    </p>
                  </div>
                ),
                progressiveDisclosure.detectedInterests.includes('financial-management') && (
                  <div key="financial-mgmt" className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                    <p>Advanced treasury optimization tools to maximize your reserve fund growth</p>
                  </div>
                ),
                progressiveDisclosure.detectedInterests.includes('analytics-reporting') && (
                  <div key="analytics" className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                    <p>
                      Comprehensive analytics dashboard for data-driven community management
                      decisions
                    </p>
                  </div>
                ),
                progressiveDisclosure.detectedInterests.includes('investment-opportunities') && (
                  <div key="investment" className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                    <p>
                      Direct access to vetted investment opportunities and wealth-building programs
                    </p>
                  </div>
                ),
                progressiveDisclosure.detectedInterests.includes('multi-property') && (
                  <div key="multi-property" className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                    <p>Multi-property management tools designed for portfolio efficiency</p>
                  </div>
                ),
                progressiveDisclosure.detectedInterests.includes('custom-integrations') && (
                  <div key="integrations" className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                    <p>Custom API integrations with your existing systems and workflows</p>
                  </div>
                ),
              ].filter(Boolean);
            })()}

          {/* Budget Alignment */}
          {formData.budgetRange && (
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
              <p>Fits comfortably within your budget range: {formData.budgetRange}</p>
            </div>
          )}

          {/* Goals Alignment */}
          {formData.primaryGoals && formData.primaryGoals.length > 0 && (
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
              <p>
                Directly supports your primary goals: {formData.primaryGoals.slice(0, 2).join(', ')}
              </p>
            </div>
          )}

          {/* Timeline Match */}
          {formData.timelineExpectation && (
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
              <p>
                Implementation timeline matches your {formData.timelineExpectation} expectations
              </p>
            </div>
          )}

          {/* ROI Estimate */}
          {parcelCount > 0 && recommendedTier !== 'custom' && (
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
              <p>
                Our platform is designed to deliver 3-5x ROI through cost savings and revenue
                optimization - let us show you how
              </p>
            </div>
          )}

          {/* Default fallback if no progressive interests */}
          {(!progressiveDisclosure.detectedInterests ||
            progressiveDisclosure.detectedInterests.length === 0) && (
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
              <p>Recommended as the most popular choice for communities of your size and type</p>
            </div>
          )}
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-linear-to-r from-green-50 to-blue-50 rounded-xl p-6 border border-green-200">
        <h4 className="text-lg font-semibold text-slate-800 mb-4">What Happens Next?</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-lg font-bold text-green-600">1</span>
            </div>
            <h5 className="font-medium text-slate-800 mb-2">Personalized Demo</h5>
            <p className="text-sm text-slate-600">
              45-minute tailored demonstration focused on your community's needs
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-lg font-bold text-blue-600">2</span>
            </div>
            <h5 className="font-medium text-slate-800 mb-2">Implementation Plan</h5>
            <p className="text-sm text-slate-600">
              Custom migration timeline and setup strategy for your community
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-lg font-bold text-purple-600">3</span>
            </div>
            <h5 className="font-medium text-slate-800 mb-2">Go Live</h5>
            <p className="text-sm text-slate-600">Launch with full training and ongoing support</p>
          </div>
        </div>
      </div>

      {/* Value Proposition */}
      <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
        <h4 className="text-lg font-semibold text-slate-800 mb-4">
          Why Communities Choose ToolboxLogic
        </h4>
        <div className="grid md:grid-cols-2 gap-6 text-sm text-slate-600">
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <div>
              <strong>Targeted Solutions:</strong> Features designed specifically for community
              management challenges
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <div>
              <strong>Proven ROI Model:</strong> Built-in cost savings through group buying and
              operational efficiencies
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <div>
              <strong>Expert Implementation:</strong> Dedicated support team ensures smooth
              transition from day one
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <div>
              <strong>Montana Optimized:</strong> Built specifically for Montana community
              management needs
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
